import { OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.types";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { Display, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_CONTAINER_COLUMN, FLEX_ITEM, H_100, POSITION, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { CustomElement, WebPageComponent } from "@cervval.js/client-html5-spa/navigation";
import { CjsButton } from "@cervval.js/client-html5-ui/src/components/button/button";
import { CjsIconFa } from "@cervval.js/client-html5-ui/src/components/icon/iconFa";
import { IconFaSize } from "@cervval.js/client-html5-ui/src/components/icon/iconFa.tools";
import { CjsSmartSelect } from "@cervval.js/client-html5-ui/src/components/smartSelect/smartSelect";
import { CjsInputTextEditor } from "@cervval.js/client-html5-ui/src/editors/inputTextEditor/inputTextEditor";
import { CatchErrorWithDialog } from "@cervval.js/client-html5-ui/src/tools/catchErrorWithDialog.decorator";
import { isPromise, PromiseOrNot } from "@cervval.js/common/src/tools/promise.tools";
import { UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { NOW } from "@cervval.js/common/src/tools/timestamps.tools.factory";
import { translateHTMLReferences } from "@ehealth_tools/client-html-tools/src/tools/webcomponent.tools";
import { LatLng, MapProfessional } from "@ehealth_tools/common-map/src/map.definitions";
import { ID } from "@ehealth_tools/common/src/data/propertyTypes/property.types";
import { MapComponent, PinStyle } from "../map/mapComponent";
import { _I18nScheduleMap } from "./scheduleMap.i18n";

type SelectType = { value: ID, label: string };
export interface ScheduleMapOptions<P extends MapProfessional> {

    googleApiToken?: string;

    googleMapUid?: string;

    /** Get professional to display */
    getProfessionals: () => PromiseOrNot<P[]>;

    title?: string;

    markerInfo?: string;

    webInterface?: boolean;

    /** Callback for RDV */
    onRDVClick?: (professional: P, ts: UnixTimestamp, productId?: ID) => void;

    /** Function to get the next RDV available date function of the professional & the product (duration) */
    getNextAvailableDate?: (professional: P, productId?: ID) => UnixTimestamp | undefined;
    /** Function to get the list of the professional product */
    getProfessionalProducts?: (professional: P) => SelectType[];
    /** How to format the product */
    formatProduct?: (selectItem: SelectType) => string;
    /** Is Part of the network */
    isMember?: (professional: P) => boolean;
    /** Extra data to display on the side of the name/company */
    displayExtraData?: (professional?: P) => HTMLElement | undefined;
    /** Get Pin Style function of professional */
    getPinStyle?: (professional: P) => PinStyle;
    /** Method to save the localization obtained by google map */
    saveLocation?: (dto: P["address"], localization: LatLng) => Promise<void>;
}


const defaultScheduleMapOptions: ScheduleMapOptions<MapProfessional> = {
    getProfessionals: () => []
};

@CustomElement({
    name: "schedule-map",
    style: require("./scheduleMap.scss"),
    template: require("./scheduleMap.html"),
    i18n: require("./scheduleMap.i18n").I18nScheduleMap(),
    elementCssClasses: [W_100, FLEX_CONTAINER_COLUMN, FLEX_ITEM, H_100, POSITION.RELATIVE]
})
export class ScheduleMap<P extends MapProfessional, I18n extends _I18nScheduleMap = _I18nScheduleMap> extends WebPageComponent<I18n> {


    @TypedWebComponentOptions(defaultScheduleMapOptions)
    public readonly options!: WebComponentOptions<ScheduleMapOptions<P>>;


    @TypedHtmlElements({ ensureLoadedClasses: [CjsSmartSelect] })
    public readonly components!: {
        title: HTMLDivElement,
        mapDisplay: HTMLDivElement,
        markerInfos: HTMLDivElement,
        proInput: CjsInputTextEditor,
        markerInput: CjsInputTextEditor,
        proIcon: CjsIconFa,
        markerIcon: CjsIconFa,
        markerInfoIcon: CjsIconFa,
        rdvButton: CjsButton,
        productSelect: CjsSmartSelect<SelectType, ID>,
        displayProInfos: HTMLDivElement,
        proName: HTMLDivElement,
        proSpecialty: HTMLDivElement,
        proAddress: HTMLDivElement,
        markerInfoDiv: HTMLDivElement,
        content: HTMLDivElement,
        filterInputs: HTMLDivElement,
        bookable: HTMLDivElement,
        notBookable: HTMLDivElement,
        extraDataContainer: HTMLDivElement
    };

    private _mapComponent!: MapComponent<P>;

    private _selectedProfessional: P | undefined;

    private _date: UnixTimestamp = NOW();

    constructor(options?: Partial<ScheduleMapOptions<P>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }


    /** @inheritdoc */
    public override init(): void {

        translateHTMLReferences(this.components, this.i18n.html);

        // Init filters
        this._initFilters();

        // Init icons
        this._initIcons();

        this.components.rdvButton.options.setAndRefreshIfChanged({
            label: this.i18n.rdvButton,
            cssClasses: ["rdvButton"]
        });

        this.components.rdvButton.clickEvent.on(_ => {
            if (this._selectedProfessional) {
                this.options.onRDVClick?.(this._selectedProfessional, this._date, this.components.productSelect.getSelectedItems()?.[0].value);
            }
        });


        // Set map component
        this._mapComponent = new MapComponent({
            apiToken: this.options.googleApiToken,
            mapID: this.options.googleMapUid,
            onClickMarkerCallback: (marker, professional) => {
                this._displayInfos(true, professional);
            },
            onClickMapCallback: () => {
                this._displayInfos(false);
            },
            getPinStyle: (pro) => {
                if (this.options.isMember?.(pro)) {
                    return {
                        background: "#045E53",
                        borderColor: "#045E53",
                        glyphColor: "#F4C96C"
                    };
                }
                return undefined;
            },
            saveLocation: this.options.saveLocation
        });
        this.components.mapDisplay.append(this._mapComponent);

        this.components.productSelect.options.setAndRefreshIfChanged({
            callbacksToOverride: {
                getItemValue: (item) => item.value,
                getItemByValue: (items, value) => items.find(v => v.value === value)
            },
            itemFormatter: (item) => {
                if (this.options.formatProduct) {
                    return this.options.formatProduct(item);
                }
                return item.label;
            }
        });

        this.components.productSelect.selectionChangeEvent.on(_ => {
            this._refreshRDVButton();
        });
    }

    /** */
    private _initFilters(): void {
        // Init pro filter (Name/Speciality)
        this.components.proInput.inputOptions.setAndRefreshIfChanged({
            placeholderText: this.i18n.filterInput.placeholderPro
        });
        const inputPro = this.components.markerInput.components.inputText;
        inputPro.inputEvent.on((evt) => {
            // TODO coach filter by Name or Speciality
        });
        // Init marker filter (Location)
        this.components.markerInput.inputOptions.setAndRefreshIfChanged({
            placeholderText: this.i18n.filterInput.placeholderMarker
        });
        const markerInput = this.components.markerInput.components.inputText;
        markerInput.inputEvent.on((evt) => {
            const input = this.components.markerInput.getInput();
            if (input.value) {
                this._mapComponent.changeMapCenter({ plainAddress: input.value }).catch(CatchErrorWithDialog);
            }
        });
    }

    /** */
    private _initIcons(): void {
        // Professional icon
        this.components.proIcon.options.setAndRefreshIfChanged({
            name: "fa-user",
            size: IconFaSize.XL
        });

        // Marker icon
        this.components.markerIcon.options.setAndRefreshIfChanged({
            name: "fa-location-dot",
            size: IconFaSize.XL
        });

        // Marker infos icon
        this.components.markerInfoIcon.options.setAndRefreshIfChanged({
            name: "fa-location-dot",
            size: IconFaSize.XL
        });
    }

    /** @inheritdoc */
    public override refresh(): void {
        this.components.title.innerHTML = this.options.title ?? this.i18n.title;
        this.components.markerInfos.innerHTML = this.options.markerInfo ?? this.i18n.markerInfos;
        toggleVisible(this.components.displayProInfos, false);
        toggleVisible(this.components.markerInfoDiv, true);

        if (this.options.webInterface) {
            this.components.filterInputs.classList.add("flex-container-row");
            this.components.filterInputs.classList.remove("flex-container-column");
        } else {
            this.components.filterInputs.classList.remove("flex-container-row");
            this.components.filterInputs.classList.add("flex-container-column");
        }

        const pRequest = this.options.getProfessionals();
        if (isPromise(pRequest)) {
            pRequest.then(professionals => {
                this._mapComponent?.options.setAndRefreshIfChanged({
                    professionals
                });
            }).catch(CatchErrorWithDialog);
        } else {
            this._mapComponent?.options.setAndRefreshIfChanged({
                professionals: pRequest
            });
        }


    }

    /**
     * Display current professional infos
     */
    private _displayInfos(show: boolean, professional?: P): void {
        toggleVisible(this.components.displayProInfos, show);
        toggleVisible(this.components.markerInfoDiv, !show);
        this.components.proName.innerHTML = professional?.displayName ?? "";
        this.components.proSpecialty.innerHTML = professional?.specialties.join(", ") ?? "";
        this.components.proAddress.innerHTML = professional?.address.name ?? "";
        this._selectedProfessional = professional;

        const proExist = professional != null;

        let hasProducts = false;
        if (proExist && this.options.getProfessionalProducts) {
            const productList = this.options.getProfessionalProducts(professional);
            this.components.productSelect.options.setAndRefreshIfChanged({
                data: productList,
                selectedValue: productList[0]?.value
            });
            hasProducts = productList.length > 0;
        }

        const proGaveConsent = proExist && this.options.isMember?.(professional);

        const isBookable = proExist && proGaveConsent && hasProducts;
        toggleVisible(this.components.bookable, isBookable, Display.FLEX);
        toggleVisible(this.components.notBookable, !isBookable);

        if (!isBookable) {
            this.components.notBookable.innerText = !proExist || !proGaveConsent ? this.i18n.noProConsent : this.i18n.noProducts;
        }
        // Extra data
        const extraData = this.options.displayExtraData?.(professional);
        toggleVisible(this.components.extraDataContainer, extraData != null);
        this.components.extraDataContainer.innerText = "";
        if (extraData) {
            this.components.extraDataContainer.append(extraData);
        }

        this._refreshRDVButton();
    }

    /** */
    private _refreshRDVButton(): void {
        if (this._selectedProfessional && this.options.getNextAvailableDate) {
            this._date = this.options.getNextAvailableDate(this._selectedProfessional, this.components.productSelect.getSelectedItems()?.[0].value) ?? NOW();
            if (this._date) {
                this.components.rdvButton.options.setAndRefreshIfChanged({
                    label: `${this.i18n.rdvButton}`
                });
            }
        }
    }

}
